import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../partials/LoadingSpinner';

import { Button, Text, Small, Panel, H4, Table, TableFigure, Badge, Input } from '@bigcommerce/big-design';
import { CheckIcon, CloseIcon, EditIcon, WarningIcon } from '@bigcommerce/big-design-icons';

import { purchaseOrderUpdateQuantity, purchaseOrderOverrideUnitCost, purchaseOrderOverrideLineTax, removeOrderLine } from '../../redux/actions/purchaseOrders';

const PurchaseOrderLine = ({ 
  payload,
  purchaseOrderLine,
  purchaseOrderLines,
  purchaseOrderType,
  quantityUpdate,
  overrideCost,
  overrideTax,
  remove,
  bigcommerceSettings,
  setModalProductId,
  setModalVariantId,
  setModalOptions,
  setModalProductName,
  setModalType,
  setModalAssignment,
  setShowAddSupplierAssignmentModal,
  activeSupplier,
  activeSuggestedPurchaseOrder,
  showSuggestionLevels,
  showUpdateExternal,
  settings }) => {

  const [adjustedUnitCost, setAdjustedUnitCost] = useState(purchaseOrderLine.supplier_price);
  const [adjustedTax, setAdjustedTax] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [lineDetail, setLineDetail] = useState(null);
  const [suggestionStatus, setSuggestionStatus] = useState(null);

  useEffect(() => {
    let detail = purchaseOrderLines.lineDetails[purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId];

    if(detail) {
      setLineDetail(detail);
      setAdjustedTax(detail.adjustedTax || '');
      setQuantity(detail.quantity);
      setAdjustedUnitCost(detail.adjustedUnitCost || purchaseOrderLine.supplier_price);
    }

    if(activeSuggestedPurchaseOrder && showSuggestionLevels) {
      let matchingLine = activeSuggestedPurchaseOrder.suggested_purchase_order_lines.suggested_purchase_order_lines.find( line => (parseInt(line.product_id) === parseInt(purchaseOrderLine.productId)) && (parseInt(line.variant_id) === parseInt(purchaseOrderLine.variantId)) )

      if(matchingLine) {
        if(matchingLine.is_low) {
          setSuggestionStatus('low');
        }

        if(matchingLine.is_critical) {
          setSuggestionStatus('critical');
        }
      }
    }
  }, [purchaseOrderLines]);

  const updateQuantity = () => {
    let value = event.target.value;
    let quantityInt = parseInt(value);
    if(quantityInt < 0) { quantityInt = 0; }

    setQuantity(quantityInt);
    quantityUpdate(payload, value, purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId)
  }

  const overrideUnitCost = (e) => {
    let value = event.target.value;
    setAdjustedUnitCost(value);
    overrideCost(payload, value, purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId)
  }

  const overrideUnitTax = (e) => {
    let value = event.target.value;
    setAdjustedTax(value);
    overrideTax(payload, value, purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId)
  }

  const format = (value) => {
    let formattedValue = "";

    if(bigcommerceSettings.currency_symbol_location === 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }
    
    formattedValue += parseFloat(value).toFixed(bigcommerceSettings.decimal_places)

    if(bigcommerceSettings.currency_symbol_location !== 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }

    formattedValue = formattedValue.replace('.', bigcommerceSettings.decimal_separator)

    return formattedValue;
  }

  const showAddSupplierAssignmentModal = () => {
    setModalProductId(purchaseOrderLine.productId);
    setModalVariantId(purchaseOrderLine.variantId);

    setModalOptions(purchaseOrderLine.options);
    setModalProductName(purchaseOrderLine.name);
    setModalType(purchaseOrderLine.variantId === null ? 'product' : 'variant');

    setShowAddSupplierAssignmentModal(true);
  }

  return(
    <>
      <div className="has-hover sku-line">
        <H4>
          { purchaseOrderLine.name || purchaseOrderLine.productName }
          { suggestionStatus &&
            <>
              &nbsp;
              &nbsp;
              <Badge label={suggestionStatus} variant={ suggestionStatus === 'critical' ? 'danger' : 'warning' } />
            </>
          }
          { purchaseOrderLine.is_external &&
            <>
              &nbsp;
              &nbsp;
              <Badge label="external" variant="warning" />
              &nbsp;
              <Button variant="utility" iconOnly={<EditIcon title="edit" />} onClick={() => showUpdateExternal(purchaseOrderLine.productId, (purchaseOrderLine.name || purchaseOrderLine.productName), purchaseOrderLine.supplier_price, purchaseOrderLine.supplier_sku)} />
            </>
          }
        </H4>
        <div className="flex-container">
          <div className="left-15">
            { purchaseOrderLine.imageUrl && <img src={purchaseOrderLine.imageUrl} /> }
          </div>
          <div className="flex-1">
            <ul>
              <li><strong>Supplier SKU</strong>: { purchaseOrderLine.supplier_sku }</li>
              <li><strong>SKU</strong>: { purchaseOrderLine.sku }</li>
              <li><strong>UPC</strong>: { purchaseOrderLine.upc }</li>
              { purchaseOrderLine?.options && purchaseOrderLine?.options.map(option =>
                  <li>
                    <strong>{ option.option_display_name || option.display_name }</strong>: <Badge label={ option.label || option.display_value } variant="secondary" />
                  </li>
                )
              }
              <li><strong>Unit Cost</strong>: <Badge label={ purchaseOrderLine.supplier_price ? format(purchaseOrderLine.supplier_price) : "Price not set" } variant={ purchaseOrderLine.supplier_price ? "success" : "warning" } /></li>
              { settings?.settings?.pack_quantities && purchaseOrderLine.packQuantity &&
                <li>
                  <strong>Pack Quantity</strong>:
                  <Badge label={ purchaseOrderLine.packQuantity } variant="success" />
                </li>
              }
            </ul>
          </div>

          <div className="flex-2">
            <div className="left-15">
              <Input 
                required 
                label="Quantity" 
                placeholder="Quantity" 
                id="quantity" 
                type="number"
                min="0"
                value={ quantity } 
                onChange={(e) => updateQuantity(e)}
              />

              { (parseInt(purchaseOrderLine.minimumOrder) > quantity) &&
                <div className="flex-container">
                  <WarningIcon color="warning60" size="medium" />
                  <Small color="warning60">
                    Quantity below MOQ: { purchaseOrderLine.minimumOrder }
                  </Small>
                </div>
              }

              { settings?.settings?.order_multiples && purchaseOrderLine.orderMultiple && (quantity % purchaseOrderLine.orderMultiple !== 0) &&
                <div className="flex-container">
                  <WarningIcon color="warning60" size="medium" />
                  <Small color="warning60">
                    Quantity not divisible by order multiple: { purchaseOrderLine.orderMultiple } ({ quantity % purchaseOrderLine.orderMultiple })
                  </Small>
                </div>
              }

              { purchaseOrderType === 'direct' && settings?.settings?.pack_quantities && (parseInt(purchaseOrderLine.packQuantity) > 1) &&
                <div className="flex-container">
                  <WarningIcon color="warning60" size="medium" />
                  <Small color="warning60">
                    Can't fulfill direct orders with pack quantities higher than 1.
                  </Small>
                </div>
              }

              <Input
                label="Adjusted Unit Cost" 
                placeholder="Adjusted Unit Cost" 
                id="adjusted_unit_cost" 
                type="number"
                min="0"
                value={ adjustedUnitCost || purchaseOrderLine.supplier_price }
                onChange={(e) => overrideUnitCost(e)}
              />

              <div className="flex-container">
                <div>Subtotal:</div>
                <div>{ format((adjustedUnitCost || purchaseOrderLine.supplier_price) * quantity) }</div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="left-15">
              <Input 
                label="Adjusted Tax Cost" 
                placeholder="Adjusted Tax Cost" 
                id="adjusted_unit_cost" 
                type="number"
                min="0"
                value={ adjustedTax }
                onChange={(e) => overrideUnitTax(e)}
                />
            </div>
          </div>

          <div className="flex-1">
            { activeSupplier && purchaseOrderType === 'direct' && purchaseOrderLine.orderMatchingAssignment &&
              <div className="left-15">
                <CheckIcon color="success" size="medium" />
                Supplier Assigned
              </div>
            }
            { activeSupplier && purchaseOrderType === 'direct' && !purchaseOrderLine.orderMatchingAssignment && ( !(parseInt(purchaseOrderLine.packQuantity) > 1) || !settings?.settings?.pack_quantities )&&
              <>
                <div className="left-15">
                  <CloseIcon color="danger" size="medium" />
                  Supplier Not Assigned
                </div>
                <Button
                  variant="utility"
                  onClick={() => showAddSupplierAssignmentModal()}
                >
                  + Add supplier assignment
                </Button>
              </>
            }

            { activeSupplier && purchaseOrderType === 'direct' && <br/> }

            <div className="left-15">
              <Button
                onClick={() => remove(purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId)}
                variant="secondary"
                actionType="destructive"
              >
                Remove
              </Button>
            </div>
            
          </div>
        </div>
      </div>      
    </>
  )

}

const mapStateToProps = state => ({
  bigcommerceSettings: state.stores.settings,
  purchaseOrderLines: state.purchaseOrderLines,
  activeSuggestedPurchaseOrder: state.suggestedPurchaseOrders.activeSuggestedPurchaseOrder,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  quantityUpdate: (payload, itemData, itemKey) => dispatch(purchaseOrderUpdateQuantity(payload, itemData, itemKey)),
  overrideCost: (payload, itemData, itemKey) => dispatch(purchaseOrderOverrideUnitCost(payload, itemData, itemKey)),
  overrideTax: (payload, itemData, itemKey) => dispatch(purchaseOrderOverrideLineTax(payload, itemData, itemKey)),
  remove: (itemKey) => dispatch(removeOrderLine(itemKey)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseOrderLine);